/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { AiType } from '@squiz/dxp-ai-types';
import { DataList, DataListItem, SdsIcon } from '@squiz/sds';
import { ICON_BOLT, ICON_QUESTION_ANSWER } from 'libs/sds/src/Icons';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AI_STRINGS } from '../../Constants/Strings.constants';
import { AiFlaggedInteractions } from '../AiFlaggedInteractions/AiFlaggedInteractions';
import { AiRatings } from '../AiRatings/AiRatings';
import { AiStatusBadge } from '../AiStatusBadge/AiStatusBadge';
import { AiDetailsCardProps } from './AiDetailsCard.props';

export const AiDetailsCard = (props: AiDetailsCardProps): JSX.Element => {
  const ai = props.ai;
  const { t } = useTranslation();

  const labels = useMemo(() => {
    return {
      authType: t(AI_STRINGS.authTypeLabel),
      lastUpdated: t(AI_STRINGS.lastUpdated),
      status: t(AI_STRINGS.status),
      [AiType.chatbot]: {
        ratingsInLast30Days: t(AI_STRINGS.ratingsInLast30Days),
        usageInLast30Days: t(AI_STRINGS.conversationsInLast30Days),
      },
      [AiType.generative]: {
        ratingsInLast30Days: t(AI_STRINGS.flaggedInLast30Days),
        usageInLast30Days: t(AI_STRINGS.usageInLast30Days),
      },
    };
  }, [t]);

  const dataListItems: Array<DataListItem> = useMemo(() => {
    const items = [
      {
        content: <span className="details-text">{labels.status}</span>,
        footer: (
          <div className="details-footer" data-id="Chatbot Status Label">
            <AiStatusBadge ai={ai} />
          </div>
        ),
        key: 'enabled-disabled',
      },
      {
        content: <span className="details-text">{labels[ai.type].usageInLast30Days}</span>,
        footer: (
          <div className="details-footer" data-id="Chatbot Conversation Record">
            {ai.type === AiType.chatbot && <SdsIcon svg={ICON_BOLT} />}
            {ai.type === AiType.generative && <SdsIcon svg={ICON_QUESTION_ANSWER} />}
            {ai.overallCount}
          </div>
        ),
        key: 'usage-in-last-30-days',
      },
      {
        content: <span className="details-text">{labels[ai.type].ratingsInLast30Days}</span>,
        footer: (
          <div className="details-footer">
            {ai.type === AiType.chatbot && <AiRatings ai={ai} />}
            {ai.type === AiType.generative && <AiFlaggedInteractions ai={ai} outline />}
          </div>
        ),
        key: 'ratings-in-last-30-days',
      },
    ];
    return items;
  }, [ai, labels]);
  return (
    <div className="ai-details-card" data-id="AI Details Card">
      <DataList empty={''} isInteractive={false} isLite items={dataListItems} showHeader={false}></DataList>
    </div>
  );
};
